/**
 * Adapter de navegación para Web
 *
 * Implementa NavigationPort usando Vue Router.
 */

import type { Router } from 'vue-router'
import type { NavigationPort, NavigationOptions, ToastOptions, ConfirmOptions } from '@pepi/core'
import { useToast } from '@/composables/useToast'
import { useConfirm } from '@/composables/useConfirm'

/**
 * Implementación de NavigationPort para Web usando Vue Router
 */
export class WebNavigationAdapter implements NavigationPort {
  private router: Router
  private toast: ReturnType<typeof useToast>
  private confirm: ReturnType<typeof useConfirm>

  constructor(router: Router) {
    this.router = router
    this.toast = useToast()
    this.confirm = useConfirm()
  }

  async navigate(path: string, options?: NavigationOptions): Promise<void> {
    if (options?.query) {
      await this.router.push({ path, query: options.query })
    } else {
      await this.router.push(path)
    }
  }

  async replace(path: string, options?: NavigationOptions): Promise<void> {
    if (options?.query) {
      await this.router.replace({ path, query: options.query })
    } else {
      await this.router.replace(path)
    }
  }

  async goBack(): Promise<void> {
    this.router.back()
  }

  getCurrentPath(): string {
    return this.router.currentRoute.value.path
  }

  async showModal(_component: unknown, _props?: Record<string, unknown>): Promise<unknown> {
    // La implementación de modales depende del sistema de modales específico
    // Por ahora usar un placeholder
    console.warn('showModal not implemented - use component-specific modal system')
    return undefined
  }

  async closeModal(_data?: unknown): Promise<void> {
    console.warn('closeModal not implemented - use component-specific modal system')
  }

  async showToast(options: ToastOptions): Promise<void> {
    switch (options.type) {
      case 'success':
        this.toast.success(options.message)
        break
      case 'error':
        this.toast.error(options.message)
        break
      case 'warning':
        this.toast.warning(options.message)
        break
      case 'info':
      default:
        this.toast.info(options.message)
        break
    }
  }

  async showConfirm(options: ConfirmOptions): Promise<boolean> {
    return this.confirm.confirm({
      title: options.title,
      message: options.message,
      confirmText: options.confirmText,
      cancelText: options.cancelText
    })
  }
}

/**
 * Crea una instancia del adapter de navegación para web
 */
export function createWebNavigationAdapter(router: Router): NavigationPort {
  return new WebNavigationAdapter(router)
}
